import {InputSelectAsl} from '@/components/commons/input-select-asl/InputSelectAsl'
import {InputSelectCity} from '@/components/commons/input-select-city/InputSelectCity'
import {InputSelectProvince} from '@/components/commons/input-select-province/InputSelectProvince'
import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@/components/ui/modal-atoms/ModalAtoms'
import {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, useForm, useFormContext, useWatch} from 'react-hook-form'
import {
    UserDomicileFormBaseSchema,
    UserDomicileFormValidationSchema,
    UserDomicileManualFormModalValidationSchema
} from '@/features/users/types'

export const UserDomicileManualFormModal = ({
    onClose,
    defaultValues
}: {
    onClose: () => void
    defaultValues: UserDomicileFormBaseSchema
}) => {
    const {t} = useTranslation()

    // validation schema

    // methods
    const parentForm = useFormContext<UserDomicileFormValidationSchema>()
    const form = useForm<UserDomicileManualFormModalValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserDomicileManualFormModalValidationSchema),
        defaultValues
    })

    // form observer
    const city = useWatch({control: form.control, name: 'domicile.city'})

    // actions
    const handleSubmit = form.handleSubmit(formValues => {
        parentForm.setValue('domicile.address', formValues.domicile?.address, {shouldDirty: true})
        parentForm.setValue('domicile.streetNumber', formValues.domicile?.streetNumber, {shouldDirty: true})
        parentForm.setValue('domicile.city', formValues.domicile?.city, {shouldDirty: true})
        parentForm.setValue('domicile.province', formValues.domicile?.province, {shouldDirty: true})
        parentForm.setValue('domicile.postalCode', formValues.domicile?.postalCode, {shouldDirty: true})
        parentForm.setValue('domicileAslDistrict', formValues.domicileAslDistrict, {shouldDirty: true})
        parentForm.clearErrors('domicile')
        onClose()
    })

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="600px" overflowY="visible">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center">
                        <ModalTitle>{t('commons:domicile')}</ModalTitle>
                        <Button shape="text" variant="ghost" onClick={onClose}>
                            <XCloseIcon />
                        </Button>
                    </Flexbox>
                </ModalHeader>
                <ModalBody>
                    <form>
                        <Grid columns={6} columnGap={2.5} rowGap={4}>
                            <GridItem startColumn={1} endColumn={4}>
                                <InputText
                                    type="text"
                                    label={t('commons:address')}
                                    placeholder={t('commons:address_placeholder')}
                                    errorMessage={form.formState.errors.domicile?.address?.message}
                                    {...form.register('domicile.address')}
                                />
                            </GridItem>
                            <GridItem startColumn={4} endColumn={5}>
                                <InputText
                                    type="text"
                                    label={t('commons:street_number')}
                                    placeholder="10"
                                    errorMessage={form.formState.errors.domicile?.streetNumber?.message}
                                    {...form.register('domicile.streetNumber')}
                                />
                            </GridItem>
                            <GridItem startColumn={1} endColumn={4}>
                                <Controller
                                    control={form.control}
                                    name="domicile.city"
                                    render={({field}) => (
                                        <InputSelectCity
                                            label={t('commons:town')}
                                            value={field.value}
                                            placeholder={t('commons:select')}
                                            errorMessage={form.formState.errors.domicile?.city?.message}
                                            onChange={value => {
                                                field.onChange(value)
                                                form.trigger('domicile.city')
                                                form.setValue('domicileAslDistrict', null)
                                                if (value?.province) {
                                                    form.setValue('domicile.province', value.province)
                                                    form.trigger('domicile.province')
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </GridItem>
                            <GridItem startColumn={4} endColumn={6}>
                                <Controller
                                    control={form.control}
                                    name="domicile.province"
                                    render={({field}) => (
                                        <InputSelectProvince
                                            label={t('commons:province')}
                                            value={field.value}
                                            errorMessage={form.formState.errors.domicile?.province?.message}
                                            placeholder={t('commons:select')}
                                            onChange={value => {
                                                field.onChange(value)
                                                form.setValue('domicile.city', null)
                                                form.setValue('domicileAslDistrict', null)
                                            }}
                                        />
                                    )}
                                />
                            </GridItem>
                            <GridItem startColumn={6} endColumn={7}>
                                <InputText
                                    type="text"
                                    label={t('commons:zip_code')}
                                    placeholder="10"
                                    errorMessage={form.formState.errors.domicile?.postalCode?.message}
                                    {...form.register('domicile.postalCode')}
                                />
                            </GridItem>
                            <GridItem startColumn={1} endColumn={4}>
                                <Controller
                                    control={form.control}
                                    name="domicileAslDistrict"
                                    render={({field}) => (
                                        <InputSelectAsl
                                            label={t('commons:domicile_asl')}
                                            value={field.value}
                                            isPortal={false}
                                            city={city ?? null}
                                            errorMessage={form.formState.errors.domicileAslDistrict?.message}
                                            placeholder={t('commons:select')}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </GridItem>
                        </Grid>
                    </form>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="secondary">
                        {t('commons:cancel')}
                    </Button>
                    <Button variant="primaryNeutral" onClick={handleSubmit}>
                        {t('commons:confirm')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
